/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Select, { components, type ControlProps, type OptionType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
	getOptionIconUrl,
	type ProjectIssueType,
	type ProjectOption,
	useProjectSearch,
} from './useProjectSearch';

const messages = defineMessages({
	noProjectsFound: {
		id: 'conversation-assistant.common.ui.project-selector.no-projects-found',
		description: 'The message for when no projects are found',
		defaultMessage: 'No projects found',
	},
	spaceSelectorLabel: {
		id: 'conversation-assistant-preview-card.common.project-selector.spaceSelectorLabel',
		description: 'The label for the space selector',
		defaultMessage: '{projectName} ({projectKey})',
	},
	searchProjectsPlaceholder: {
		id: 'conversation-assistant-preview-card.common.project-selector.searchProjectsPlaceholder',
		description: 'The placeholder for the search projects input',
		defaultMessage: 'Search projects',
	},
	projectSelectorLabel: {
		id: 'conversation-assistant-preview-card.common.project-selector.projectSelectorLabel',
		description: 'The visible label for the project selector',
		defaultMessage: 'Project',
	},
});

const styles = cssMap({
	customControl: {
		width: '100%',
	},
	dropdownContent: {
		maxHeight: '250px',
		minWidth: '285px',
		overflowY: 'auto',
	},
	searchContainer: {
		paddingInline: token('space.150'),
	},
	projectSelectorIconOnlyDisabled: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '1.5rem',
		width: '1.5rem',
	},
});

type BaseProjectSelectorProps = {
	siteId: string;
	setProject: (args: {
		projectId: string;
		projectKey: string;
		projectName: string;
		issueTypes: ProjectIssueType[];
	}) => void;
	selectedProject?: ProjectOption;
	selectedProjectIconUrl?: string;
	isDisabled?: boolean;
};

/**
 * Returns a memoized icon component used by both selector variants. Falls back to
 * the default `SpacesIcon` when no project icon URL has been provided.
 */
const useProjectIcon = (selectedProjectIconUrl?: string) =>
	useCallback(
		(iconProps: Parameters<typeof SpacesIcon>[0]) =>
			selectedProjectIconUrl ? (
				<img src={selectedProjectIconUrl} alt="" aria-hidden={true} width={16} height={16} />
			) : (
				<SpacesIcon {...iconProps} />
			),
		[selectedProjectIconUrl],
	);

const CustomControl = ({ children, ...props }: ControlProps<OptionType>) => {
	const { projectIcon: ProjectIcon } = props.selectProps as {
		projectIcon?: React.ComponentType<React.ComponentProps<typeof SpacesIcon>>;
	};

	return (
		<components.Control {...props}>
			<Box paddingInline="space.100" xcss={styles.customControl}>
				<Inline alignBlock="center">
					{ProjectIcon ? <ProjectIcon label="" /> : <SpacesIcon label="" />}
					{children}
				</Inline>
			</Box>
		</components.Control>
	);
};

/**
 * Icon-only variant: renders an `IconButton` trigger that opens a `DropdownMenu`
 * with an inline search field. Mirrors the Jira modernised create experience
 * (`IssueCreateModernisedProjectPickerDropdown`).
 */
const ProjectSelectorIconOnly = ({
	siteId,
	setProject,
	selectedProject,
	selectedProjectIconUrl,
	isDisabled = false,
}: BaseProjectSelectorProps): JSX.Element => {
	const {
		displayedOptions,
		isLoading,
		sortOptionsWithSelectedFirst,
		handleProjectChange,
		debouncedLoadOptions,
		handleMenuOpen,
		handleMenuClose,
	} = useProjectSearch({ siteId, isDisabled, selectedProject, setProject });
	const { formatMessage } = useIntl();

	const [searchValue, setSearchValue] = useState('');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const firstItemRef = useRef<HTMLElement | null>(null);
	const searchInputRef = useRef<HTMLInputElement | null>(null);

	const ProjectIcon = useProjectIcon(selectedProjectIconUrl);

	const handleSearchInputChange = useCallback(
		(event: React.FormEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setSearchValue(value);
			debouncedLoadOptions(value);
		},
		[debouncedLoadOptions],
	);

	const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		// Tab or ArrowDown moves focus from the input to the first dropdown item.
		if ((event.key === 'Tab' && !event.shiftKey) || event.key === 'ArrowDown') {
			event.preventDefault();
			event.stopPropagation();
			firstItemRef.current?.focus();
			return;
		}
		// Stop typing keys from being interpreted as DropdownMenu keyboard navigation
		// (otherwise letters would jump focus between menu items).
		if (event.key !== 'Escape' && !(event.key === 'Tab' && event.shiftKey)) {
			event.stopPropagation();
		}
	}, []);

	const handleSearchMouseDown = useCallback((event: React.MouseEvent) => {
		// Prevent DropdownMenu from stealing focus when clicking inside the search field.
		event.stopPropagation();
	}, []);

	const handleDropdownContentKeyDown = useCallback((event: React.KeyboardEvent) => {
		// ArrowUp / Shift+Tab on the first item returns focus to the search input.
		if (
			firstItemRef.current &&
			event.target === firstItemRef.current &&
			(event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey))
		) {
			event.preventDefault();
			event.stopPropagation();
			searchInputRef.current?.focus();
		}
	}, []);

	const handleDropdownOpenChange = useCallback(
		(args: { isOpen: boolean }) => {
			setIsDropdownOpen(args.isOpen);
			if (args.isOpen) {
				handleMenuOpen();
			} else {
				setSearchValue('');
				handleMenuClose();
			}
		},
		[handleMenuOpen, handleMenuClose],
	);

	const handleSearchInputRef = useCallback((element: HTMLInputElement | null) => {
		searchInputRef.current = element;
	}, []);

	useEffect(() => {
		if (isDropdownOpen) {
			searchInputRef.current?.focus();
		}
	}, [isDropdownOpen]);

	const triggerLabel = selectedProject
		? formatMessage(messages.spaceSelectorLabel, {
				projectName: selectedProject?.label,
				projectKey: selectedProject?.projectKey,
			})
		: undefined;

	const renderTrigger = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
			<Tooltip content={triggerLabel} position="top" hideTooltipOnClick>
				{(tooltipProps) =>
					isDisabled ? (
						<Box
							{...tooltipProps}
							aria-label={triggerLabel}
							xcss={styles.projectSelectorIconOnlyDisabled}
						>
							<ProjectIcon label="" />
						</Box>
					) : (
						<span {...tooltipProps}>
							<IconButton
								{...triggerProps}
								ref={triggerRef}
								icon={ProjectIcon}
								spacing="compact"
								appearance="subtle"
								label={triggerLabel}
								isTooltipDisabled
								testId="jira-aifc-preview-card-project-selector-trigger"
							/>
						</span>
					)
				}
			</Tooltip>
		),
		[ProjectIcon, triggerLabel, isDisabled],
	);

	const sortedOptions = sortOptionsWithSelectedFirst(displayedOptions);
	const showEmptyState = !isLoading && sortedOptions.length === 0;

	return (
		<DropdownMenu
			trigger={renderTrigger}
			placement="bottom-start"
			onOpenChange={handleDropdownOpenChange}
			spacing="compact"
			shouldRenderToParent
			autoFocus={false}
			testId="jira-aifc-preview-card-project-selector"
		>
			<Box xcss={styles.dropdownContent} onKeyDown={handleDropdownContentKeyDown}>
				<DropdownItemGroup>
					<Box xcss={styles.searchContainer} onMouseDown={handleSearchMouseDown}>
						<TextField
							ref={handleSearchInputRef}
							placeholder={formatMessage(messages.searchProjectsPlaceholder)}
							value={searchValue}
							onChange={handleSearchInputChange}
							onKeyDown={handleSearchKeyDown}
							elemBeforeInput={<SearchIcon label="" size="medium" />}
							elemAfterInput={isLoading ? <Spinner size="small" /> : undefined}
							isCompact
							appearance="none"
							autoComplete="off"
							testId="jira-aifc-preview-card-project-selector-search"
						/>
					</Box>
				</DropdownItemGroup>
				{showEmptyState ? (
					<DropdownItem isDisabled>{formatMessage(messages.noProjectsFound)}</DropdownItem>
				) : (
					<DropdownItemGroup hasSeparator>
						{sortedOptions.map((option, index) => {
							const iconUrl = getOptionIconUrl(option);
							return (
								<DropdownItem
									key={String(option.value)}
									ref={index === 0 ? firstItemRef : undefined}
									isSelected={option.value === selectedProject?.value}
									onClick={() => handleProjectChange(option)}
									elemBefore={
										iconUrl ? (
											<img src={iconUrl} alt="" aria-hidden={true} width={24} height={24} />
										) : undefined
									}
								>
									{option.label}
								</DropdownItem>
							);
						})}
					</DropdownItemGroup>
				)}
			</Box>
		</DropdownMenu>
	);
};

/**
 * Full width variant: Standard `<Select>`-based variant of the project selector with an
 * inline search field.
 */
const ProjectSelectorFullWidth = ({
	siteId,
	setProject,
	selectedProject,
	selectedProjectIconUrl,
	isDisabled = false,
}: BaseProjectSelectorProps): JSX.Element => {
	const {
		displayedOptions,
		isLoading,
		sortOptionsWithSelectedFirst,
		handleProjectChange,
		debouncedLoadOptions,
		handleMenuOpen,
		handleMenuClose,
	} = useProjectSearch({ siteId, isDisabled, selectedProject, setProject });

	const ProjectIcon = useProjectIcon(selectedProjectIconUrl);

	const handleInputChange = useCallback(
		(inputValue: string, { action }: { action: string }) => {
			if (action === 'input-change') {
				debouncedLoadOptions(inputValue);
			}
		},
		[debouncedLoadOptions],
	);

	const guardedHandleMenuOpen = useCallback(() => {
		if (isDisabled) {
			return;
		}
		handleMenuOpen();
	}, [isDisabled, handleMenuOpen]);
	const { formatMessage } = useIntl();

	return (
		<Select
			projectIcon={ProjectIcon}
			label={formatMessage(messages.projectSelectorLabel)}
			testId="jira-aifc-preview-card-project-selector"
			spacing="compact"
			appearance="default"
			value={selectedProject}
			isLoading={isLoading}
			options={sortOptionsWithSelectedFirst(displayedOptions)}
			onChange={handleProjectChange}
			onInputChange={handleInputChange}
			onMenuOpen={guardedHandleMenuOpen}
			onMenuClose={handleMenuClose}
			components={{
				Control: CustomControl,
			}}
			isDisabled={isDisabled}
			{...(isDisabled ? { menuIsOpen: false } : {})}
			styles={{
				// Raise the menu above the work items table's fade gradient and "Show more" button.
				menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 200 }),
				control: (base: Record<string, unknown>) => ({
					...base,
					borderColor: token('color.border'),
				}),
			}}
		/>
	);
};

/**
 * This component is used to select a project from Jira.
 * The icon only variant is used by the single work item preview card view.
 * Whereas the full width variant is used by the multi work item preview card view.
 *
 * @param siteId - The site ID of the Jira instance.
 * @param setProject - The function to set the project.
 * @param selectedProject - The selected project.
 * @param isDisabled - Whether the project selector is disabled.
 */
export const ProjectSelector = ({
	isIconOnly = false,
	...rest
}: BaseProjectSelectorProps & {
	isIconOnly?: boolean;
}): JSX.Element =>
	isIconOnly ? <ProjectSelectorIconOnly {...rest} /> : <ProjectSelectorFullWidth {...rest} />;
